// Auth wrapper. Magic-link only (no passwords). The current session is exposed
// via `getSession()` / `onAuthChange()` so other modules don't need to know
// about Supabase types.

import type { Session } from "@supabase/supabase-js";
import { getSupabase } from "./client";

let currentSession: Session | null = null;
const listeners: ((s: Session | null) => void)[] = [];

export async function initAuth(): Promise<Session | null> {
  const sb = getSupabase();
  if (!sb) return null;
  const { data } = await sb.auth.getSession();
  currentSession = data.session;
  sb.auth.onAuthStateChange((_event, session) => {
    currentSession = session;
    for (const fn of listeners) fn(session);
  });
  return currentSession;
}

export function getSession(): Session | null { return currentSession; }
export function getUserId(): string | null { return currentSession?.user.id ?? null; }

export function onAuthChange(fn: (s: Session | null) => void): void {
  listeners.push(fn);
}

export async function signInWithEmail(email: string): Promise<{ error?: string }> {
  const sb = getSupabase();
  if (!sb) return { error: "backend not configured" };
  const { error } = await sb.auth.signInWithOtp({
    email,
    options: { emailRedirectTo: window.location.origin },
  });
  return error ? { error: error.message } : {};
}

export type OAuthProvider = "google" | "github" | "discord";

/**
 * Begins an OAuth sign-in flow. The browser is redirected to the provider and
 * back to `window.location.origin`. Returns only on error (otherwise the page
 * unloads).
 */
export async function signInWithOAuth(provider: OAuthProvider): Promise<{ error?: string }> {
  const sb = getSupabase();
  if (!sb) return { error: "backend not configured" };
  const { error } = await sb.auth.signInWithOAuth({
    provider,
    options: { redirectTo: window.location.origin },
  });
  return error ? { error: error.message } : {};
}

export async function signOut(): Promise<void> {
  const sb = getSupabase();
  if (!sb) return;
  await sb.auth.signOut();
}
